import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Input, Select, Icon } from '@st-operations/uikit/src/v2'

import './styles.scss'

import ValidatedForm from '_components/validated-form/index.jsx'
import CustomSelect from '_components/custom-select/index.jsx'
import {
  SECRETS_MANAGER_ARN_EXAMPLE,
  SECRETS_MANAGER_ARN_INVALID_MESSAGE,
} from '_lib/secretsManagerArn.js'

const body = typeof document !== 'undefined' ? document.querySelector('body') : null

/**
 * Delivery pathways for the Unified User Profile delivery destination. A single
 * `deliveryMethod` selector replaces the legacy destination + access-method split.
 * Each method drives which credential fields are required (see DeliveryContent).
 */
const DELIVERY_METHOD_OPTIONS = [
  {
    label: 'S3 Internal',
    value: 's3-internal',
  },
  {
    label: 'S3 External (Access Keys)',
    value: 's3-external-access-keys',
  },
  {
    label: 'S3 External (Secrets Manager ARN)',
    value: 's3-external-secrets-manager-arn',
  },
  {
    label: 'GCS (Credentials)',
    value: 'gcs-credentials',
  },
  {
    label: 'GCS (Secrets Manager ARN)',
    value: 'gcs-secrets-manager-arn',
  },
]

const DeliveryDestinationConfigurationForm = ({
  data = {},
  onDataChange,
  sharethisS3PathOptions = [],
  requiredErrorDeliveryMethod,
  requiredErrorDestinationName,
  requiredErrorRemoteS3Path,
  requiredErrorS3AccessKeyId,
  requiredErrorS3SecretAccessKeyId,
  requiredErrorCredsSecretArn,
  invalidErrorCredsSecretArn,
  requiredErrorBucket,
  requiredErrorProject,
  requiredErrorRemotePath,
  requiredErrorServiceAccount,
}) => {
  const deliveryMethod = data.deliveryMethod

  const isExternalAccessKeys = deliveryMethod === 's3-external-access-keys'
  const isSecretsManager =
    deliveryMethod === 's3-external-secrets-manager-arn' ||
    deliveryMethod === 'gcs-secrets-manager-arn'
  const isGcsCredentials = deliveryMethod === 'gcs-credentials'

  const handleSelectChange = (_option, name, value) => {
    onDataChange?.({ ...data, [name]: value })
  }

  const handleChange = (value, name) => {
    onDataChange?.({ ...data, [name]: value })
  }

  return (
    <ValidatedForm
      className="pb-20"
      inputs={[
        {
          label: 'Delivery Method',
          required: requiredErrorDeliveryMethod,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Delivery Method
              </span>
              <Select
                name="deliveryMethod"
                value={data.deliveryMethod}
                options={DELIVERY_METHOD_OPTIONS}
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorDeliveryMethod,
                })}
                onChange={handleSelectChange}
                isSearchable
                menuPortalTarget={body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    maxHeight: 200,
                  }),
                }}
                useReactSelect
              />
            </>
          ),
        },
        {
          label: 'S3 Path',
          required: requiredErrorBucket,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: true,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                S3 Path
              </span>
              <div className={classNames('delivery-destination-configuration__content-box')}>
                <CustomSelect
                  className={classNames(
                    'delivery-destination-configuration__content-select--name',
                    {
                      'delivery-destination-configuration__required-field': requiredErrorBucket,
                    }
                  )}
                  options={sharethisS3PathOptions}
                  name="s3Path"
                  value={data.s3Path}
                  onChange={(_, name, value) => handleChange(value, name)}
                  placeholder="Select a S3 path..."
                  menuPortalTarget={body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      maxHeight: 200,
                    }),
                  }}
                />
              </div>
            </>
          ),
        },
        {
          label: 'Name',
          required: requiredErrorDestinationName,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: true,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Name
              </span>
              <div className={classNames('delivery-destination-configuration__content-box')}>
                <Input
                  className={classNames(
                    'delivery-destination-configuration__content-select--name',
                    {
                      'delivery-destination-configuration__required-field': requiredErrorDestinationName,
                    }
                  )}
                  name="deliveryName"
                  onChange={handleChange}
                  value={data.deliveryName}
                  placeholder="your-destination-name"
                />
                <span
                  className={classNames(
                    'delivery-destination-configuration__content-label',
                    'delivery-destination-configuration__content-label--s3-path',
                    'block text-[12px] mt-2'
                  )}
                >
                  {`Path to generated files: s3://${data.s3Path || '<bucket>'}/${
                    data.deliveryName || '<name>'
                  }/`}
                </span>
              </div>
            </>
          ),
        },
        {
          label: 'Remote S3 Path',
          required: requiredErrorRemoteS3Path,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isExternalAccessKeys,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Remote S3 Path
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorRemoteS3Path,
                })}
                name="remoteS3Path"
                onChange={handleChange}
                value={data.remoteS3Path}
              />
            </>
          ),
        },
        {
          label: 'S3 Access Key ID',
          required: requiredErrorS3AccessKeyId,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isExternalAccessKeys,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                S3 Access Key ID
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorS3AccessKeyId,
                })}
                name="s3AccessKeyId"
                onChange={handleChange}
                value={data.s3AccessKeyId}
              />
            </>
          ),
        },
        {
          label: 'S3 Secret Access Key',
          required: requiredErrorS3SecretAccessKeyId,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isExternalAccessKeys,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                S3 Secret Access Key
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorS3SecretAccessKeyId,
                })}
                name="s3SecretAccessKey"
                onChange={handleChange}
                value={data.s3SecretAccessKey}
              />
            </>
          ),
        },
        {
          label: 'Secrets Manager ARN',
          required: requiredErrorCredsSecretArn,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isSecretsManager,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Secrets Manager ARN
              </span>
              <div className="delivery-destination-configuration__content-box">
                <Input
                  className={classNames(
                    'delivery-destination-configuration__content-select--name',
                    {
                      'delivery-destination-configuration__required-field':
                        requiredErrorCredsSecretArn || invalidErrorCredsSecretArn,
                    }
                  )}
                  name="credsSecretArn"
                  onChange={handleChange}
                  value={data.credsSecretArn}
                  placeholder={SECRETS_MANAGER_ARN_EXAMPLE}
                />
                <If condition={invalidErrorCredsSecretArn}>
                  <div className="field-validation-message">
                    <Icon type="fill" icon="error" className="field-validation-message__icon" />
                    <span className="field-validation-message__desc">
                      {SECRETS_MANAGER_ARN_INVALID_MESSAGE}
                    </span>
                  </div>
                </If>
              </div>
            </>
          ),
        },
        {
          label: 'Project',
          required: requiredErrorProject,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isGcsCredentials,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Project
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorProject,
                })}
                name="gcsProject"
                onChange={handleChange}
                value={data.gcsProject}
              />
            </>
          ),
        },
        {
          label: 'Remote Path',
          required: requiredErrorRemotePath,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isGcsCredentials,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Remote Path
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorRemotePath,
                })}
                name="gcsRemotePath"
                onChange={handleChange}
                value={data.gcsRemotePath}
              />
            </>
          ),
        },
        {
          label: 'Service Account',
          required: requiredErrorServiceAccount,
          validationClassName:
            'delivery-destination-configuration__required--destination-configuration',
          show: isGcsCredentials,
          render: () => (
            <>
              <span
                className={classNames(
                  'delivery-destination-configuration__content-label',
                  'delivery-destination-configuration__content-label--destination-configuration'
                )}
              >
                Service Account
              </span>
              <Input
                className={classNames('delivery-destination-configuration__content-select', {
                  'delivery-destination-configuration__required-field': requiredErrorServiceAccount,
                })}
                name="gcsServiceAccount"
                onChange={handleChange}
                value={data.gcsServiceAccount}
              />
            </>
          ),
        },
      ]}
    />
  )
}

DeliveryDestinationConfigurationForm.propTypes = {
  data: PropTypes.object,
  onDataChange: PropTypes.func,
  sharethisS3PathOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  requiredErrorDeliveryMethod: PropTypes.bool,
  requiredErrorDestinationName: PropTypes.bool,
  requiredErrorRemoteS3Path: PropTypes.bool,
  requiredErrorS3AccessKeyId: PropTypes.bool,
  requiredErrorS3SecretAccessKeyId: PropTypes.bool,
  requiredErrorCredsSecretArn: PropTypes.bool,
  invalidErrorCredsSecretArn: PropTypes.bool,
  requiredErrorBucket: PropTypes.bool,
  requiredErrorProject: PropTypes.bool,
  requiredErrorRemotePath: PropTypes.bool,
  requiredErrorServiceAccount: PropTypes.bool,
}

export default DeliveryDestinationConfigurationForm
